import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, from, Subject } from 'rxjs';
import { Permission } from '@core/permissions/models/permission.enum';
import {
  DialogService,
  DialogSize,
  MenuKeyboardNavigationDirective,
  PanelTriggerDirective,
  SpinnerComponent,
  TooltipDirective,
  PrefetchLinkDirective,
} from '@kaseya-design-components';
import { PermissionDirective } from '@core/permissions/directives/permission.directive';
import { helpLinks, HelpLink } from '@layout/help-menu/models/help-link.interface';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { MedalliaFacade } from '@core/medallia/facades/medallia.facade';
import { RouterLink } from '@angular/router';
import { WINDOW } from '@kaseya-utils';

@Component({
  selector: 'itc-help-menu',
  templateUrl: 'help-menu.component.html',
  styleUrl: 'help-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    PermissionDirective,
    PanelTriggerDirective,
    MenuKeyboardNavigationDirective,
    SpinnerComponent,
    TooltipDirective,
    RouterLink,
    PrefetchLinkDirective,
  ],
})
export class HelpMenuComponent {
  private readonly dialogService = inject(DialogService);
  private readonly medalliaFacade = inject(MedalliaFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly window = inject(WINDOW);
  private readonly destroyRef = inject(DestroyRef);

  readonly helpLinks: Record<keyof typeof helpLinks, HelpLink> = helpLinks;
  readonly Permission = Permission;
  readonly closeMenu$ = new Subject<void>();
  readonly isDialogLoading = signal(false);

  readonly hasAccessToMedalliaIntegrationView = this.permissionsFacade.hasPermission(Permission.MedalliaIntegrationView);
  readonly hasAccessToLegalTermsArchiveView = this.permissionsFacade.hasPermission(Permission.LegalTermsArchiveView);

  isPanelOpened = false;

  openLink(link: string): void {
    this.closeMenu$.next();
    this.window.open(link, '_blank', 'noopener,noreferrer');
  }

  provideFeedback(): void {
    this.medalliaFacade.showMedalliaSurvey();
  }

  openVideoTutorialsDialog(): void {
    if (this.isDialogLoading()) {
      return;
    }

    this.isDialogLoading.set(true);
    this.loadAndOpenTutorialsDialog();
  }

  private loadAndOpenTutorialsDialog(): void {
    from(import('@layout/tutorials/components/tutorials-dialog/tutorials-dialog.component'))
      .pipe(
        finalize(() => {
          this.isDialogLoading.set(false);
          this.closeMenu$.next();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ TutorialsDialogComponent }) => {
        this.dialogService.openDialog(TutorialsDialogComponent, {
          width: DialogSize.Lg,
          styleClass: 'dialog dialog--full-height',
        });
      });
  }
}
